// src/components/team/CheckRolesPermissionModal.tsx

import React, { useState, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';
import { toast } from 'react-toastify';

type CheckRolesPermissionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Updated to handle both old and new API response structures
type PermissionResponse =
  | {
    categories?: Record<string, string[]>;  // Old structure
    [key: string]: any;
  }
  | any[];  // New structure: array of modules

type Role = {
  id: string;
  tenantId: string;
  name: string;
  systemRole: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
  version: number;
  archived: boolean;
  deletedAt: string | null;
};

type RolesResponse = {
  roles: Role[];
};

// Map API permission keys to display labels
const permissionLabels: Record<string, string> = {
  'campaigns.create': 'Create Campaign',
  'campaigns.edit': 'Edit Campaign',
  'campaigns.VIEW': 'View Campaigns',
  'campaigns.CREATE': 'Create Campaign',
  'campaigns.EDIT': 'Edit Campaign',
  'campaigns.DELETE': 'Delete Campaigns',
  'campaigns.read': 'View Campaigns',
  'campaigns.schedule': 'Schedule Campaigns',
  'campaigns.links.manage': 'Manage Campaign Links',
  'campaigns.links.read': 'View Campaign Links',
  'campaigns.send_history.read': 'View Send History',
  'automations.create': 'Create Automation',
  'automations.edit': 'Edit Automation',
  'automations.VIEW': 'View Automations',
  'automations.CREATE': 'Create Automation',
  'automations.EDIT': 'Edit Automation',
  'automations.DELETE': 'Delete Automations',
  'automations.read': 'View Automations',
  'automations.publish': 'Publish Automations',
  'automations.validate': 'Validate Automations',
  'automations.executions.read': 'View Executions',
  'automations.lifecycle.manage': 'Manage Lifecycle',
  'templates.create': 'Create Templates',
  'templates.view': 'View Templates',
  'templates.VIEW': 'View Templates',
  'templates.CREATE': 'Create Templates',
  'templates.EDIT': 'Edit Templates',
  'templates.DELETE': 'Delete Templates',
  'templates.update': 'Update Templates',
  'templates.delete': 'Delete Templates',
  'templates.duplicate': 'Duplicate Templates',
  'templates.send_test': 'Send Test Emails',
  'templates.test_mail.read': 'View Test Mails',
  'team.invite': 'Invite Team Member',
  'team.members.manage': 'Manage Team Members',
  'team.roles.manage': 'Manage Roles',
  'team.ownership.transfer': 'Transfer Ownership',
  'team.ownership.request': 'Request Ownership',
  'team.view': 'View Team',
  'team.audit.read': 'View Audit Logs',
  'team.roles.read': 'View Roles',
  'team.members.read': 'View Members',
  'team.ownership.read': 'View Ownership',
  'team.permission_catalog.read': 'View Permissions',
  'team.invite.accept': 'Accept Invites',
  'audience.VIEW': 'View Audience',
  'audience.CREATE': 'Create Audience',
  'audience.EDIT': 'Edit Audience',
  'audience.DELETE': 'Delete Audience',
  'audiences.VIEW': 'View Audiences',
  'audiences.CREATE': 'Create Audiences',
  'audiences.EDIT': 'Edit Audiences',
  'audiences.DELETE': 'Delete Audiences',
  'audience.contacts.manage': 'Manage Contacts',
  'audience.contacts.read': 'View Contacts',
  'audience.segments.manage': 'Manage Segments',
  'audience.segments.read': 'View Segments',
  'audience.imports.manage': 'Manage Imports',
  'audience.imports.read': 'View Imports',
  'audience.exports.manage': 'Manage Exports',
  'audience.exports.read': 'View Exports',
  'audience.tags.manage': 'Manage Tags',
  'audience.tags.read': 'View Tags',
  'audience.bulk.manage': 'Manage Bulk Operations',
  'audience.bulk.read': 'View Bulk Operations',
  'audience.subscriptions.manage': 'Manage Subscriptions',
  'audience.subscriptions.read': 'View Subscriptions',
  'audience.insights.read': 'View Insights',
  'audience.segment_snapshots.manage': 'Manage Snapshots',
  'audience.segment_snapshots.read': 'View Snapshots',
  'reports.VIEW': 'View Reports',
  'reports.CREATE': 'Create Reports',
  'reports.EDIT': 'Edit Reports',
  'reports.DELETE': 'Delete Reports',
  'analytics.read': 'View Analytics',
  'analytics.exports.create': 'Create Exports',
  'analytics.exports.read': 'View Exports',
  'analytics.reports.read': 'View Reports',
  'settings.integrations.read': 'View Integrations',
  'settings.privacy.read': 'View Privacy Settings',
  'settings.notifications.manage': 'Manage Notifications',
  'settings.notifications.read': 'View Notifications',
  'settings.security.read': 'View Security Settings',
  'settings.billing.read': 'View Billing',
  'settings.api_keys.read': 'View API Keys',
  'profile.read': 'View Profile',
  'profile.photo.manage': 'Manage Profile Photo',
  'identity.password.change': 'Change Password',
  'identity.user.credentials.read': 'View Credentials',
  'identity.user.security.read': 'View Security',
  'identity.account.email_change': 'Change Email',
  'identity.security.verify': 'Verify Security',
  'notifications.manage': 'Manage Notifications',
  'notifications.read': 'View Notifications',
  'activity.feed.read': 'View Activity Feed',
  'org.settings.read': 'View Org Settings',
  'org.settings.manage': 'Manage Org Settings',
  'tenant.internal.read': 'View Tenant',
  'kafka.dlq.replay': 'Replay DLQ',
  'kafka.dlq.triage': 'Triage DLQ',
  'kafka.dlq.read': 'View DLQ',
  'kafka.analytics': 'View Analytics',
  'kafka.testing.admin': 'Testing Admin',
  'kafka.audit': 'View Audit',
  'kafka.admin': 'Kafka Admin',
  'admin.broadcast.manage': 'Manage Broadcast',
  'sender_profiles.manage': 'Manage Sender Profiles',
  'sender_profiles.read': 'View Sender Profiles',
};

// Normalize permission key for comparison (convert to lowercase for case-insensitive matching)
const normalizePermissionKey = (key: string): string => {
  return key.toLowerCase();
};

const CheckRolesPermissionModal: React.FC<CheckRolesPermissionModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [permissionsData, setPermissionsData] = useState<PermissionResponse | null>(null);
  const [rolesData, setRolesData] = useState<Role[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch permissions and roles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }


      const [permissionsResponse, rolesResponse] = await Promise.all([
        fetch(`${BASE_URL}/v1/team/permissions/catalog`, {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }),
        fetch(`${BASE_URL}/v1/team/roles`, {
          method: 'GET',
          headers: {
            'X-Tenant-Id': tenantId,
            'X-User-Id': userId || '',
            'Authorization': `Bearer ${token}`,
          },
        }),
      ]);

      if (!permissionsResponse.ok) {
        if (permissionsResponse.status === 403) {
          throw new Error('You do not have permission to view permissions');
        }
        throw new Error(`Failed to fetch permissions: ${permissionsResponse.status}`);
      }

      if (!rolesResponse.ok) {
        if (rolesResponse.status === 403) {
          throw new Error('You do not have permission to view roles');
        }
        throw new Error(`Failed to fetch roles: ${rolesResponse.status}`);
      }

      const permissionsData = await permissionsResponse.json();
      const rolesData: RolesResponse = await rolesResponse.json();

      setPermissionsData(permissionsData);
      setRolesData(rolesData.roles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
      // console.error('Error fetching data:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Check if a role has a specific permission (case-insensitive)
  const hasPermission = (roleName: string, permissionKey: string): boolean => {
    const role = rolesData.find((r) => r.name === roleName);
    if (!role) return false;

    const normalizedPermissionKey = normalizePermissionKey(permissionKey);
    return role.permissions.some(
      (p) => normalizePermissionKey(p) === normalizedPermissionKey
    );
  };

  // UPDATED: Get grouped permissions from the data (handles both old and new structures)
  const getGroupedPermissions = (): Record<string, string[]> => {
    if (!permissionsData) return {};

    const grouped: Record<string, string[]> = {};

    // Check if it's the new structure (array of modules)
    if (Array.isArray(permissionsData)) {
      permissionsData.forEach((module) => {
        const category = module.module || 'Other';
        const permissions: string[] = [];

        if (module.permissions && Array.isArray(module.permissions)) {
          module.permissions.forEach((perm: any) => {
            if (perm.code) {
              permissions.push(perm.code);
            }
          });
        }

        if (permissions.length > 0) {
          grouped[category] = permissions;
        }
      });
    }
    // Check if it's the old structure (with categories)
    else if (permissionsData && typeof permissionsData === 'object' && 'categories' in permissionsData) {
      const categories = permissionsData.categories;
      if (categories && typeof categories === 'object') {
        Object.entries(categories).forEach(([category, permissions]) => {
          if (Array.isArray(permissions)) {
            grouped[category] = permissions;
          }
        });
      }
    }
    // Fallback: try to extract from any structure
    else {
      console.warn('Unknown permissions data structure in CheckRolesPermissionModal:', permissionsData);
      // Try to extract from object values
      if (permissionsData && typeof permissionsData === 'object') {
        Object.entries(permissionsData).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            const permissions: string[] = [];
            value.forEach((item) => {
              if (typeof item === 'string') {
                permissions.push(item);
              } else if (item && typeof item === 'object' && item.code) {
                permissions.push(item.code);
              }
            });
            if (permissions.length > 0) {
              grouped[key] = permissions;
            }
          }
        });
      }
    }

    return grouped;
  };

  // Closes the modal
  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Render loading state
  if (loading) {
    return (
      <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50'>
        <div className='bg-white w-[900px] max-h-[90vh] rounded-lg shadow-lg relative'>
          <div className='flex justify-between items-center px-6 py-4 border-b'>
            <div>
              <h2 className='text-lg font-semibold'>Check Role Permissions</h2>
              <p className='text-sm text-gray-500'>
                This table shows what each role have permission to do.
              </p>
            </div>
            <button
              onClick={handleClose}
              className='text-gray-500 hover:text-gray-700'
            >
              <FiXComponent size={20} />
            </button>
          </div>
          <div className='p-6 text-center'>
            <div className='flex justify-center items-center py-8'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD]'></div>
              <span className='ml-2 text-gray-500'>Loading permissions...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50'>
        <div className='bg-white w-[900px] max-h-[90vh] rounded-lg shadow-lg relative'>
          <div className='flex justify-between items-center px-6 py-4 border-b'>
            <div>
              <h2 className='text-lg font-semibold'>Check Role Permissions</h2>
              <p className='text-sm text-gray-500'>
                This table shows what each role have permission to do.
              </p>
            </div>
            <button
              onClick={handleClose}
              className='text-gray-500 hover:text-gray-700'
            >
              <FiXComponent size={20} />
            </button>
          </div>
          <div className='p-6 text-center'>
            <p className='text-red-500'>{error}</p>
            <button
              onClick={fetchData}
              className='mt-4 px-4 py-2 bg-[#00A4BD] text-white rounded hover:bg-[#00899E] transition-colors'
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get grouped permissions
  const groupedPermissions = getGroupedPermissions();

  // Get unique role names from the roles data, sorted with Owner first
  const roleNames = [
    'Owner',
    'Admin',
    ...rolesData
      .filter((r) => r.name !== 'Owner' && r.name !== 'Admin')
      .map((r) => r.name),
  ];

  // Get display label for permission, fallback to the key itself
  const getDisplayLabel = (permKey: string): string => {
    return permissionLabels[permKey] || permKey;
  };

  // Check if there are any permissions to display
  const hasPermissions = Object.keys(groupedPermissions).length > 0;

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black/50 z-50'>
      <div className='bg-white w-[900px] max-h-[90vh] rounded-lg shadow-lg relative'>
        {/* Header */}
        <div className='flex justify-between items-center px-6 py-4 border-b'>
          <div>
            <h2 className='text-lg font-semibold'>Check Role Permissions</h2>
            <p className='text-sm text-gray-500'>
              This table shows what each role have permission to do.
            </p>
          </div>
          <button
            onClick={handleClose}
            className='text-gray-500 hover:text-gray-700'
          >
            <FiXComponent size={20} />
          </button>
        </div>

        {/* Table wrapper with BOTH axis scroll */}
        <div className='p-6'>
          {hasPermissions ? (
            <div className='max-h-[60vh] border border-gray-200 rounded-md overflow-auto'>
              <table className='w-full text-sm min-w-[700px] border-collapse'>
                <thead className='bg-gray-100 text-gray-600 text-[13px]'>
                  <tr>
                    {/* Corner – highest z so nothing covers it */}
                    <th className='px-4 py-3 text-left font-normal sticky top-0 left-0 bg-gray-100 z-40 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]'>
                      MODULES
                    </th>
                    {roleNames.map((role) => (
                      <th
                        key={role}
                        className='px-4 py-3 text-center font-normal whitespace-nowrap sticky top-0 bg-gray-100 z-30'
                      >
                        {role.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {Object.entries(groupedPermissions).map(
                    ([category, permissions]) => (
                      <React.Fragment key={category}>
                        <tr>
                          {/* Category label – higher z than the empty cells next to it */}
                          <td className='px-4 py-2 font-semibold text-gray-700 sticky top-[45px] left-0 bg-gray-50 z-30 border-b min-w-[200px] whitespace-nowrap shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]'>
                            {category.toUpperCase()}
                          </td>
                          {roleNames.map((role) => (
                            <td
                              key={role}
                              className='px-4 py-2 sticky top-[45px] bg-gray-50 z-20 border-b'
                            />
                          ))}
                        </tr>

                        {permissions.map((permKey) => {
                          const displayLabel = getDisplayLabel(permKey);
                          return (
                            <tr key={permKey} className='border-b hover:bg-gray-50 group'>
                              <td className='px-4 py-2 text-gray-700 sticky left-0 bg-white group-hover:bg-gray-50 z-10 min-w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]'>
                                {displayLabel}
                              </td>
                              {roleNames.map((role) => (
                                <td
                                  key={role}
                                  className='px-4 py-2 text-center bg-white group-hover:bg-gray-50'
                                >
                                  {hasPermission(role, permKey) ? (
                                    <span className='text-green-600 font-bold text-lg'>✓</span>
                                  ) : (
                                    <span className='text-red-500 font-bold text-lg'>✗</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-center py-8 text-gray-500'>
              No permissions data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckRolesPermissionModal;